import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  Company,
  CompanyInput,
  CompanyType,
} from '@fullyops/data/companies/company';
import { MaterialModule } from '@fullyops/shared/material.module';
import { I18NextCapPipe } from 'angular-i18next';

export interface CompanyAssignee {
  id: string;
  firstName: string;
  lastName: string;
}

export interface CompanyCountry {
  id: string;
  isoCode: string;
  name: string;
}

@Component({
  selector: 'fo-company-form',
  imports: [MaterialModule, ReactiveFormsModule, I18NextCapPipe],
  templateUrl: './company-form.component.html',
  styleUrl: './company-form.component.scss',
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', floatLabel: 'always' },
    },
  ],
})
export class CompanyFormComponent implements OnInit, OnChanges {
  @Input() initialValue?: Company;
  @Input() emailRequired = false;
  @Input() bothTypeAllowed = false;
  @Input() potentialAssignees: CompanyAssignee[] = [];
  @Input() countries: CompanyCountry[] = [];
  @Input() disabled = false;

  @Output() onSave = new EventEmitter<CompanyInput>();

  protected formGroup = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    externalId: new FormControl('', { nonNullable: true }),
    type: new FormControl<CompanyType | null>(null, {
      validators: Validators.required,
    }),
    nif: new FormControl('', { nonNullable: true }),
    assignees: new FormControl<{ id: string }[]>([], { nonNullable: true }),
    country: new FormControl<CompanyCountry | null>(null),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.email],
    }),
    phone: new FormControl('', { nonNullable: true }),
    website: new FormControl('', { nonNullable: true }),
    notes: new FormControl('', { nonNullable: true }),
  });

  setError(
    fieldName: keyof typeof this.formGroup.controls,
    message: string,
  ): void {
    this.formGroup.controls[fieldName].setErrors(
      { message },
      { emitEvent: false },
    );
  }

  ngOnInit(): void {
    const v = this.initialValue;
    if (v != null) {
      this.formGroup.setValue(
        {
          name: v.name,
          externalId: v.externalId,
          type: v.type,
          nif: v.nif,
          // Unlike most other legacy entities, the backend does not return assignees
          // or countries as nested entities. The IDs are the only thing that matters,
          // though.
          assignees: v.assigneeIds.map((id) => ({ id })),
          country:
            v.countryId == null
              ? null
              : { id: v.countryId, isoCode: v.countryISOCode, name: '' },
          email: v.email,
          phone: v.mobileNumber,
          website: v.website,
          notes: v.notes,
        },
        { emitEvent: false },
      );
    }
    if (this.disabled) {
      this.formGroup.disable({ emitEvent: false });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.emailRequired != null) {
      if (changes.emailRequired.currentValue) {
        this.formGroup.controls.email.addValidators(Validators.required);
      } else {
        this.formGroup.controls.email.removeValidators(Validators.required);
      }
    }
  }

  protected idsEqual(a: { id: string }, b: { id: string }): boolean {
    return a?.id === b?.id;
  }

  protected onClickSave(): void {
    const v = this.formGroup.value;
    this.onSave.emit({
      name: v.name,
      externalId: v.externalId,
      type: v.type,
      nif: v.nif,
      assigneeIds: v.assignees.map((a) => a.id),
      countryISOCode: v.country?.isoCode,
      email: v.email,
      mobileNumber: v.phone,
      website: v.website,
      notes: v.notes,
    });
  }
}
